import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, SignIn, CaretDown, CaretRight } from "@phosphor-icons/react";
import { useAuth, ROLE_PREVIEW_PRESETS, applyRolePreview } from "../../lib/auth";
import { NAV_GROUPS, userCanSeeGroup, visibleItems, firstAllowedPath } from "../../pages/DashboardLayout";

const GROUP_LABEL = { pilotage: "Accueil & pilotage", dojo: "Dojo & élèves", competition: "Compétition & événements", crm: "CRM & relations", marketing: "Marketing & contenu", finance: "Finance & administration", boutique: "Boutique & monétisation", patrimoine: "Patrimoine & sinistre", systeme: "IA, système & support", pkh: "PowerKick Her" };

function pagesFor(fakeUser) {
  return NAV_GROUPS.filter((g) => userCanSeeGroup(g, fakeUser)).map((g) => ({ id: g.id, label: g.label, items: visibleItems(g, fakeUser) }));
}

function RoleRow({ id, label, preview, who }) {
  const { realUser, setRolePreview } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const fake = useMemo(() => applyRolePreview(realUser, preview), [realUser, preview]);
  const groups = useMemo(() => pagesFor(fake), [fake]);
  const landing = firstAllowedPath(fake);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const test = () => { setRolePreview(preview); navigate(landing); };
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/30" data-testid={`role-access-${id}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-medium text-zinc-100" data-testid={`role-access-toggle-${id}`}>
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />} {label}
          {who && <span className="text-[11px] text-zinc-500 font-normal">· {who}</span>}
        </button>
        <span className="text-[11px] text-zinc-500">{total} page{total > 1 ? "s" : ""} · {groups.length} pôle{groups.length > 1 ? "s" : ""}</span>
        <span className="ml-auto flex items-center gap-1.5 text-[11px] text-zinc-400"><SignIn size={13} /> Page de connexion : <Link to={landing} className="text-amber-300 hover:underline" data-testid={`role-access-landing-${id}`}>{landing}</Link></span>
        <button onClick={test} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500/15 px-2.5 py-1.5 text-xs font-medium text-amber-300 hover:bg-amber-500/25" data-testid={`role-access-test-${id}`}><Eye size={14} /> Tester la connexion comme ce rôle</button>
      </div>
      {open && (
        <div className="grid gap-3 border-t border-zinc-800 px-4 py-3 md:grid-cols-2" data-testid={`role-access-pages-${id}`}>
          {groups.map((g) => (
            <div key={g.id}>
              <div className="mb-1 text-[11px] uppercase tracking-wider text-zinc-500">{GROUP_LABEL[g.id] || g.label}</div>
              <ul className="space-y-0.5">
                {g.items.map((it) => <li key={it.to}><Link to={it.to} className="text-xs text-zinc-300 hover:text-amber-300 hover:underline">{it.label}</Link> <span className="text-[10px] text-zinc-600">{it.to}</span></li>)}
              </ul>
            </div>
          ))}
          {!groups.length && <p className="text-xs text-zinc-500">Aucune page ouverte à ce rôle.</p>}
        </div>
      )}
    </div>
  );
}

export function RoleAccessMatrix({ team = [] }) {
  const customs = team.filter((m) => m.role === "custom");
  return (
    <section className="space-y-3" data-testid="role-access-matrix">
      <div>
        <h2 className="text-base font-semibold text-zinc-200 flex items-center gap-2"><Eye size={18} className="text-amber-400" /> Pages et connexion par rôle</h2>
        <p className="text-xs text-zinc-500">Pour chaque rôle : la page d'atterrissage à la connexion, la liste des pages ouvertes (lien direct) et un aperçu réel de la navigation — sans changer de compte.</p>
      </div>
      <div className="space-y-2">
        {Object.entries(ROLE_PREVIEW_PRESETS).map(([role, p]) => <RoleRow key={role} id={role} label={p.label} preview={{ role, label: p.label }} />)}
        {customs.map((m) => <RoleRow key={m.email} id={`custom-${m.email}`} label={`Accès personnalisé — ${m.full_name || m.email}`} who={(m.allowed_groups || []).map((g) => GROUP_LABEL[g] || g).join(" · ")} preview={{ role: "custom", label: `Personnalisé · ${m.full_name || m.email}`, allowed_groups: m.allowed_groups || [] }} />)}
      </div>
    </section>
  );
}
